import { useCallback, useState } from 'react';
import { streamText } from 'ai';
import { LINE_ITEM_FIELDS } from '../utils/lineItemImport.utils';
import { models, activatedModel } from '../../Campaigns/campaigndetails/components/LLMIntegration/LLMIntegrationTool';

/**
 * Hook for AI-powered column matching
 * Analyzes Excel headers and sample data to suggest field mappings with confidence scores
 */
export const useAIColumnMatching = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  /**
   * Get available fields for dropdown selection
   */
  const getFieldOptions = useCallback(() => {
    return [
      { value: null, label: '-- Skip --' },
      ...Object.entries(LINE_ITEM_FIELDS).map(([key, def]) => ({
        value: key,
        label: `${def.label}${def.required ? ' *' : ''}`,
      })),
    ];
  }, []);

  /**
   * Build the field descriptions for the AI prompt
   */
  const getFieldDescriptions = useCallback(() => {
    return Object.entries(LINE_ITEM_FIELDS).map(([key, def]) => ({
      key,
      label: def.label,
      type: def.type,
      required: def.required,
      examples: def.csvColumns.slice(0, 3).join(', '),
    }));
  }, []);

  /**
   * Analyze columns using AI to suggest mappings
   * @param {string[]} headers - Column headers from the Excel file
   * @param {Array<Object>} sampleRows - First few rows of data for context
   * @returns {Promise<Object>} - Mapping suggestions with confidence scores
   */
  const analyzeColumnsWithAI = useCallback(async (headers, sampleRows = []) => {
    // Check if AI is available
    if (!models || !models[activatedModel]) {
      console.warn('AI not available, falling back to rule-based matching');
      return null;
    }

    setIsAnalyzing(true);
    setAnalysisError(null);

    try {
      const fieldDescriptions = getFieldDescriptions();

      // Build sample data preview (first 3 rows)
      const sampleDataPreview = sampleRows.slice(0, 3).map((row, idx) => {
        const rowData = {};
        headers.forEach(header => {
          rowData[header] = row.data?.[header] || '';
        });
        return `Row ${idx + 1}: ${JSON.stringify(rowData)}`;
      }).join('\n');

      const systemPrompt = `You are a data mapping assistant. Your task is to analyze Excel column headers and suggest which line item fields they should map to.

Available target fields:
${fieldDescriptions.map(f => `- ${f.key}: "${f.label}" (${f.type}${f.required ? ', required' : ''}) - Example column names: ${f.examples}`).join('\n')}

Rules:
1. Each Excel column can only map to ONE target field or be skipped
2. Return confidence scores from 0-100 based on how certain you are
3. Use 100 for exact matches, 80-99 for very likely matches, 60-79 for probable matches, below 60 for uncertain
4. If a column doesn't match any field (like "Targeting", "Bridge", etc.), set field to null and confidence to 0
5. Consider both the column header name AND the sample data when determining matches

Respond ONLY with valid JSON in this exact format (no markdown, no explanation):
{
  "mappings": [
    {"header": "Column Name", "field": "fieldKey or null", "confidence": 85, "reason": "brief reason"}
  ]
}`;

      const userPrompt = `Analyze these Excel columns and suggest field mappings:

Headers: ${JSON.stringify(headers)}

Sample data:
${sampleDataPreview || 'No sample data available'}

Return the JSON mapping for each header.`;

      const result = streamText({
        model: models[activatedModel],
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
      });

      const response = await result.text;

      // Parse the JSON response
      let parsed;
      try {
        // Try to extract JSON from the response (in case there's extra text)
        const jsonMatch = response.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          parsed = JSON.parse(jsonMatch[0]);
        } else {
          throw new Error('No JSON found in response');
        }
      } catch (parseError) {
        console.error('Failed to parse AI response:', response);
        throw new Error('Failed to parse AI response');
      }

      // Convert to the expected mapping format
      const mappings = {};
      if (parsed.mappings && Array.isArray(parsed.mappings)) {
        parsed.mappings.forEach(item => {
          mappings[item.header] = {
            field: item.field,
            confidence: item.confidence / 100, // Convert to 0-1 scale
            reason: item.reason,
            aiSuggested: true,
          };
        });
      }

      setIsAnalyzing(false);
      return mappings;

    } catch (error) {
      console.error('AI column analysis failed:', error);
      setAnalysisError(error.message);
      setIsAnalyzing(false);
      return null;
    }
  }, [getFieldDescriptions]);

  /**
   * Check if AI is available
   */
  const isAIAvailable = useCallback(() => {
    return !!(models && models[activatedModel]);
  }, []);

  return {
    getFieldOptions,
    analyzeColumnsWithAI,
    isAnalyzing,
    analysisError,
    isAIAvailable,
  };
};

export default useAIColumnMatching;
